/**
 * Voice Call Window Component
 *
 * Floating window that displays when user is in a DM voice call:
 * - Shows current participants (from LiveKit)
 * - Connection status
 * - Voice controls (mute, deafen, disconnect)
 * - Screen share viewer (LiveKit-native)
 * - Error messages
 */

import React, { useEffect, useRef } from 'react';
import { useVoice } from '@/contexts/VoiceContext';
import { useLiveKit } from '@/contexts/LiveKitContext';
import { useUserSettings } from '@/hooks/useUserSettings';
import { soundService } from '@/services/sound.service';
import { VoiceControls } from './VoiceControls';
import { ScreenShareViewer } from './ScreenShareViewer';

// Map of keyboard codes to display labels
const KEY_LABELS: Record<string, string> = {
  Space: 'SPACE',
  ControlLeft: 'LEFT CTRL',
  ControlRight: 'RIGHT CTRL',
  ShiftLeft: 'LEFT SHIFT',
  ShiftRight: 'RIGHT SHIFT',
  AltLeft: 'LEFT ALT',
  AltRight: 'RIGHT ALT',
  KeyV: 'V',
  KeyT: 'T',
};

export const VoiceCallWindow: React.FC = () => {
  const { voiceState, leaveSession } = useVoice();
  const { state: liveKitState } = useLiveKit();
  const { data: userSettings } = useUserSettings();
  const ringbackRef = useRef<{ stop: () => void } | null>(null);

  const participants = liveKitState.participants;
  const hasScreenShares = liveKitState.screenShares.length > 0;

  // Caller "ringing" state: media is connected but no one else has joined yet.
  // The local participant is always present, so <= 1 means the callee hasn't answered.
  const isConnecting = voiceState.isConnecting || liveKitState.isConnecting;
  const isRinging =
    !!liveKitState.dmThreadId && liveKitState.isConnected && !isConnecting && participants.length <= 1;

  // Ringback tone while ringing — loops until answered or the call ends
  useEffect(() => {
    if (isRinging) {
      if (!ringbackRef.current) {
        const volume = userSettings?.call_ringtone_volume ?? 70;
        ringbackRef.current = soundService.playRingtone('callRingtone', volume);
      }
    } else {
      ringbackRef.current?.stop();
      ringbackRef.current = null;
    }
    return () => {
      ringbackRef.current?.stop();
      ringbackRef.current = null;
    };
  }, [isRinging, userSettings?.call_ringtone_volume]);

  // Don't render if no active DM call session
  if (!voiceState.activeSession || !liveKitState.dmThreadId) {
    return null;
  }

  return (
    <>
      {/* Screen Share Viewer (overlay mode) — LiveKit-native, works correctly now */}
      {hasScreenShares && (
        <ScreenShareViewer
          className="fixed inset-0 z-50"
          showParticipantName={true}
          autoFullscreen={true}
        />
      )}

      {/* Voice Call Window */}
    <div className="fixed bottom-4 right-4 bg-gray-800 text-white rounded-lg shadow-xl p-4 w-80 z-50 animate-in slide-in-from-bottom-4 duration-300">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">
          {voiceState.activeSession?.sessionType === 'group' ? 'Групово обаждане' : 'Обаждане'}
        </h3>
        <button
          onClick={leaveSession}
          className="text-gray-400 hover:text-white transition-colors"
          title="Прекрати обаждането"
          aria-label="Прекрати обаждането"
        >
          ✕
        </button>
      </div>

      {/* Connection status */}
      {isConnecting && (
        <div className="mb-4 text-yellow-400 text-sm flex items-center space-x-2">
          <span className="inline-block w-2 h-2 bg-yellow-400 rounded-full animate-pulse" />
          <span>Свързване...</span>
        </div>
      )}

      {/* Ringing status — caller waiting for the callee to answer */}
      {!isConnecting && isRinging && (
        <div className="mb-4 text-green-400 text-sm flex items-center space-x-2">
          <span className="inline-block w-2 h-2 bg-green-400 rounded-full animate-ping" />
          <span>Звъни…</span>
        </div>
      )}

      {/* Error message */}
      {(voiceState.error || liveKitState.error) && (
        <div className="mb-4 p-3 bg-red-900/50 border border-red-600 rounded text-sm text-red-200">
          {voiceState.error || liveKitState.error}
        </div>
      )}

      {/* Participants (from LiveKit) */}
      <div className="space-y-2 mb-4 max-h-48 overflow-y-auto">
        {participants.length === 0 ? (
          <p className="text-gray-400 text-sm text-center py-4">
            {isRinging ? 'Звъни…' : 'Изчакване на участници...'}
          </p>
        ) : (
          participants.map((participant) => (
            <div
              key={participant.userId}
              className="flex items-center justify-between p-2 bg-gray-700/50 rounded-lg"
            >
              <div className="flex items-center space-x-2">
                <div className={`w-2 h-2 rounded-full ${participant.isSpeaking ? 'bg-green-400 animate-pulse' : 'bg-gray-500'}`} />
                <span className="text-sm truncate max-w-[180px]">
                  {participant.displayName}
                </span>
              </div>
              <div className="flex items-center space-x-1">
                {participant.isMuted && (
                  <svg className="w-4 h-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
                  </svg>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Controls */}
      <VoiceControls />

      {/* Connection info */}
      <div className="mt-4 pt-4 border-t border-gray-700 space-y-2">
        <div className="flex items-center justify-between text-xs text-gray-400">
          <span>
            {participants.length}{' '}
            {participants.length === 1 ? 'участник' : 'участника'}
          </span>
          <div className="flex gap-2">
            {liveKitState.isMuted && <span className="text-red-400">Без звук</span>}
            {liveKitState.isDeafened && <span className="text-red-400">Заглушен</span>}
            {liveKitState.isScreenSharing && <span className="text-blue-400">Споделяне</span>}
          </div>
        </div>

        {/* Push-to-talk indicator */}
        {userSettings?.push_to_talk_enabled && (
          <div className="flex items-center justify-center gap-2 p-2 bg-blue-900/30 border border-blue-600/50 rounded text-xs text-blue-300">
            <span>Push to Talk:</span>
            <kbd className="px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white font-semibold">
              {KEY_LABELS[userSettings.push_to_talk_key] || userSettings.push_to_talk_key}
            </kbd>
          </div>
        )}
      </div>
    </div>
    </>
  );
};
